/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ErrorService } from 'src/common/error.service';
import { isEmpty } from 'lodash-es';
import {
  ClientUnitPermissionsModel, PageableUsers,
  PageableResponse, SubjectViewModel, UnitPermissionsModel, SharedWithResponse,
} from './share-panel.d';
import { setInheritedToBoolean } from './utilities';
import { SaveAdapter, SerialSaveService } from '../../save/serial-save.service';

const defaultPageableResponse = {
  hasContent: true,
  hasNext: false,
  hasPrevious: false,
  isFirst: true,
  isLast: true,
  number: 1,
  numberOfElements: 1,
  size: 1,
  totalElements: 1,
  totalPages: 1,
};

const emptyPageableResponse = {
  content: [],
  hasContent: false,
  hasNext: false,
  hasPrevious: false,
  isFirst: true,
  isLast: true,
  number: 0,
  numberOfElements: 0,
  size: 0,
  totalElements: 0,
  totalPages: 0,
};

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(
    private http: HttpClient,
    private errorSvc: ErrorService,
    private serialSaveService: SerialSaveService,
  ) { }

  searchAccountUsernamesAndEmails(partialUsernameOrEmail: string, page?: number): Observable<any | PageableUsers> {
    if (!this.isPartialUsernameOrEmailEmpty(partialUsernameOrEmail)) {
      return partialUsernameOrEmail.includes('@')
        ? this.searchAccountEmails(partialUsernameOrEmail)
        : this.searchAccountUsernames(partialUsernameOrEmail, page);
    }
    return of(emptyPageableResponse);
  }

  searchAccountUsernames(partialUsername: string, page: number): Observable<any | PageableUsers> {
    return this.http
      .get('accounts/view', {
        params: {
          partialUsername,
          page,
        },
      })
      .pipe(
        map((resp: PageableResponse): PageableResponse => resp),
      );
  }

  searchAccountEmails(address: string): Observable<any | PageableUsers> {
    return this.http
      .get('accounts/view/email', {
        params: {
          address,
        },
      })
      .pipe(
        // The client requires this endpoint to return a PageableResponse,
        // so we map this response to fit the model.
        // This response is used in the same UI locations as searchAccountUsernames.
        map((resp: SubjectViewModel): PageableResponse => (
          isEmpty(resp) ? emptyPageableResponse
            : {
              content: [{
                displayName: address,
                username: address,
                id: resp?.id,
                permissionType: 'BULB_USER',
              }],
              ...defaultPageableResponse,
            })),
      );
  }

  fetchGroupsForSharing(): Observable<PageableUsers> {
    return this.http
      .get('groups/view', {
        params: {
          page: 0,
          size: 1000,
        },
      }) as Observable<PageableUsers>;
  }

  fetchRecentlySharedWith(userId: string): Observable<PageableResponse> {
    return this.http
      .get(`recent/${userId}/sharedwith`)
      .pipe(
        // The API may return null, but we need an array to iterate over.
        map((resp: any): SharedWithResponse => {
          if (resp.accountsRecentlySharedWith === null) {
            return {
              accountId: resp.accountId,
              accountsRecentlySharedWith: [],
            };
          }
          return resp;
        }),
        // The client requires this endpoint to return a PageableResponse,
        // so we map this response to fit the model.
        // This response is used in the same UI locations as searchAccountUsernames.
        map((resp: SharedWithResponse): PageableResponse => ({
          content: resp.accountsRecentlySharedWith,
          ...defaultPageableResponse,
        })),
      );
  }

  getPermissions(unitId: string): Observable<UnitPermissionsModel> {
    return this.http
      .get(`units/${unitId}/share`)
      .pipe(
        // The response may return string values of "true" or "false" for 'inherited' fields.
        // This map converts the string values to booleans.
        map(setInheritedToBoolean),
      );
  }

  savePermissions(unitId: string, settings: ClientUnitPermissionsModel): Observable<UnitPermissionsModel> {
    const filteredSettings = settings;

    // Server returns an error if fields other than id and permissionType are sent.
    filteredSettings.permissions = settings.permissions
      .map(({
        id, permissionType, displayName, inherited, createNew, collection, parentGroupId,
      }) => ({
        id, permissionType, displayName, inherited, createNew, collection, parentGroupId,
      }));

    // Something was causing permission IDs to be null.
    // Filter out null/undefined IDs before sending to server.
    if (filteredSettings.permissions.find(({ id }) => !id)) {
      filteredSettings.permissions = filteredSettings.permissions.filter(({ id }) => !!id);
      this.errorSvc.captureErrorMessage('Permission IDs were null. They have been removed.', settings);
    }

    return from(this.serialSaveService.save({
      save: () => this.http
        .post(
          `units/${unitId}/share`,
          { ...filteredSettings },
        )
        .pipe(
          // The response may return string values of "true" or "false" for 'inherited' fields.
          // This map converts the string values to booleans.
          map(setInheritedToBoolean),
        )
        .toPromise(),
    } as SaveAdapter)) as Observable<UnitPermissionsModel>;

  }

  createNewShareableLink(unitId: string): Observable<any> {
    return from(this.serialSaveService.save({
      save: () => this.http
        .post(`units/${unitId}/shareableLink`, {})
        .toPromise(),
    } as SaveAdapter)) as Observable<UnitPermissionsModel>;
  }

  /**
   * Return true if username or email is empty string or null else false.
   * @param partialUsernameOrEmail
   * @returns true/false.
   */
  isPartialUsernameOrEmailEmpty(partialUsernameOrEmail: any): boolean {
    return (partialUsernameOrEmail === null
      || partialUsernameOrEmail.length === 0
      || partialUsernameOrEmail.trim() === '');
  }
}
